/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, ItemType, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetParam';

const ActionSetParamManager: ActionManagerFn<'ActionSetParam'> = (
  platform: Platform,
): ActionManager<'ActionSetParam'> => {
  return {
    ...baseActionManager(platform, 'ActionSetParam'),
    defaultValue,
    props: {
      ...baseActionProps,
      key: {
        label: 'Name',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
      value: {
        label: 'Value',
        type: 'expression',
        required: true,
        returnType: async () => 'any',
      },
    },
    name: 'Set Param',
    icon: { iconName: 'value-absolute' },
    getDisplayText: (item) =>
      typeof item.props.key === 'string'
        ? item.props.key
        : item.props.key.value,
    description: `**Overview**<br/>
The Set Param action is a universal utility component designed to pass data arguments to "parent" actions. It functions as a key-value builder, allowing developers to define specific inputs, payloads, or query parameters that the parent action needs to execute correctly.<br/><br/>
This component is structurally dependent and cannot exist independently. Its behavior changes entirely based on the parent it is placed within. For example, when placed inside a "Go To Page" action, it constructs a URL query string; when placed inside an "Invoke Cloud Function" action, it constructs a JSON payload. It is the primary mechanism for passing dynamic context (like IDs or status flags) between different parts of the application logic.

**Key Features**<br/>
* **Contextual Adaptation:** Automatically formats the parameter based on the parent action's requirements (e.g., as a URL parameter, a function argument, or event data).
* **Strict Parent Scope:** restricted via isAllowedInsideParent to work only with specific actions: ActionGoToPage, ActionInvokeCloudFunction, ActionInvokeUIScript, ActionPublishEvent, ActionRunJS, and UploadFileToFunction.
* **Dynamic Definition:** Both the key (Name) and value properties support expressions, allowing parameters to be defined dynamically at runtime (e.g., passing a variable ID as the value).

**Properties**<br/>
* **Name (key) (Required):** An expression property returning a string.
  * *Function:* The identifier for the parameter (e.g., 'orderId' or 'viewMode').
* **Value (Required):** An expression property returning any.
  * *Function:* The data content to be passed. Can be a string, number, boolean, or object, depending on what the parent action expects.

**Use Cases**<br/>
* **Navigation:** Inside ActionGoToPage, defining key: 'id', value: '101' results in the browser navigating to .../page?id=101.
* **API Calls:** Inside ActionInvokeCloudFunction, defining key: 'amount', value: 500 ensures the server function receives { amount: 500 } in its request body.
* **Custom Scripts:** Inside ActionRunJS, passing local variables into the execution scope of the custom script.`,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        [
          'ActionGoToPage',
          'ActionInvokeCloudFunction',
          'ActionInvokeUIScript',
          'ActionPublishEvent',
          'ActionRunJS',
          'UploadFileToFunction',
        ] as ItemType[]
      ).includes(parent.type);
    },
  };
};

export default ActionSetParamManager;
